(function() {
  'use strict';
  
  const DEBUG_MODE = false; // Set to false in production
  const PERF_TRACKING = true; // Track performance
  
  const ATTRIBUTION_CONFIG = {
    parameters: {
      gclid: 'gclid', fbclid: 'fbclid', msclkid: 'msclkid', 
      ttclid: 'ttclid', twclid: 'twclid'
    },
    storage: { key: 'attribution_data', expireDays: 90 }
  };

  // Performance tracker
  const perf = {
    start: (label) => PERF_TRACKING ? performance.mark(`${label}-start`) : null,
    end: (label) => {
      if (!PERF_TRACKING) return;
      performance.mark(`${label}-end`);
      performance.measure(label, `${label}-start`, `${label}-end`);
      const measure = performance.getEntriesByName(label)[0];
      if (DEBUG_MODE) console.log(`⏱️ ${label}: ${measure.duration.toFixed(2)}ms`);
    }
  };

  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  }

  function captureAttribution() {
    perf.start('captureAttribution');
    const urlParams = new URLSearchParams(window.location.search);
    const currentAttribution = getStoredAttribution() || {};
    let captured = [];

    Object.entries(ATTRIBUTION_CONFIG.parameters).forEach(([key, param]) => {
      const value = urlParams.get(param);
      if (value) {
        currentAttribution[key] = value;
        currentAttribution[`${key}_timestamp`] = new Date().toISOString();
        captured.push(`${key}=${value.substring(0, 10)}...`);
      }
    });

    if (captured.length > 0) {
      storeAttribution(currentAttribution);
      if (DEBUG_MODE) console.log('✅ Captured:', captured.join(', '));
    }
    
    perf.end('captureAttribution');
    return currentAttribution;
  }

  function captureMetaData() {
    perf.start('captureMetaData');
    const data = {
      user_agent: navigator.userAgent,
      fbp: getCookie('_fbp'),
      fbc_cookie: getCookie('_fbc'),
      screen_width: window.screen?.width,
      screen_height: window.screen?.height,
      browser_language: navigator.language,
      referrer: document.referrer || undefined,
      landing_page: window.location.href
    };
    
    try {
      data.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (e) {}
    
    perf.end('captureMetaData');
    return data;
  }

  function getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    return parts.length === 2 ? parts.pop().split(';').shift() : null;
  }

  function storeAttribution(attribution) {
    perf.start('storeAttribution');
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + ATTRIBUTION_CONFIG.storage.expireDays);
    
    const data = { ...attribution, expires: expirationDate.toISOString() };

    try {
      localStorage.setItem(ATTRIBUTION_CONFIG.storage.key, JSON.stringify(data));
      
      const cookieValue = encodeURIComponent(JSON.stringify(data));
      const maxAge = ATTRIBUTION_CONFIG.storage.expireDays * 24 * 60 * 60;
      document.cookie = `${ATTRIBUTION_CONFIG.storage.key}=${cookieValue}; path=/; max-age=${maxAge}; SameSite=Lax`;
      
      if (DEBUG_MODE) console.log('💾 Stored attribution data');
    } catch (e) {
      console.warn('Storage failed:', e);
    }
    perf.end('storeAttribution');
  }

  function getStoredAttribution() {
    try {
      const stored = localStorage.getItem(ATTRIBUTION_CONFIG.storage.key);
      if (stored) {
        const attribution = JSON.parse(stored);
        if (new Date(attribution.expires) > new Date()) {
          return attribution;
        }
      }
    } catch (e) {}
    return null;
  }

  function getAllData() {
    perf.start('getAllData');
    const attribution = getStoredAttribution();
    const metaData = captureMetaData();
    const combined = { ...attribution, ...metaData };
    
    if (attribution) {
      if (attribution.gclid) combined.attribution_source = 'google_ads';
      else if (attribution.fbclid) combined.attribution_source = 'facebook_ads';
      else if (attribution.msclkid) combined.attribution_source = 'microsoft_ads';
      else if (attribution.ttclid) combined.attribution_source = 'tiktok_ads';
      else if (attribution.twclid) combined.attribution_source = 'twitter_ads';
    }
    
    delete combined.expires;
    Object.keys(combined).forEach(key => {
      if (key.includes('_url')) delete combined[key];
    });
    
    perf.end('getAllData');
    return combined;
  }

  function addAttributionToCart() {
    perf.start('addAttributionToCart');
    const data = getAllData();
    if (Object.keys(data).length === 0) {
      if (DEBUG_MODE) console.log('⏭️ No data to add to cart');
      perf.end('addAttributionToCart');
      return;
    }
    
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: data })
    })
    .then(response => {
      if (response.ok && DEBUG_MODE) {
        console.log('✅ Added to cart:', Object.keys(data).length, 'fields');
      }
    })
    .catch(() => {})
    .finally(() => perf.end('addAttributionToCart'));
  }

  const addAttributionToForms = debounce(function() {
    perf.start('addAttributionToForms');
    const data = getAllData();
    if (Object.keys(data).length === 0) {
      perf.end('addAttributionToForms');
      return;
    }
    
    const forms = document.querySelectorAll('form[action*="/cart"]');
    let formsUpdated = 0;
    
    forms.forEach(form => {
      if (form.hasAttribute('data-attribution-added')) return;
      form.setAttribute('data-attribution-added', 'true');
      
      Object.entries(data).forEach(([key, value]) => {
        if (value) {
          const field = document.createElement('input');
          field.type = 'hidden';
          field.name = `attributes[${key}]`;
          field.value = String(value);
          form.appendChild(field);
        }
      });
      formsUpdated++;
    });
    
    if (DEBUG_MODE && formsUpdated > 0) {
      console.log(`📝 Updated ${formsUpdated} form(s) with ${Object.keys(data).length} fields`);
    }
    perf.end('addAttributionToForms');
  }, 250);

  const originalFetch = window.fetch;
  window.fetch = function(url, options) {
    if (typeof url === 'string' && url.includes('/cart/add')) {
      perf.start('fetchIntercept');
      const data = getAllData();
      if (options?.body && Object.keys(data).length > 0) {
        try {
          const body = JSON.parse(options.body);
          body.attributes = { ...body.attributes, ...data };
          options.body = JSON.stringify(body);
          if (DEBUG_MODE) console.log('🎯 Intercepted /cart/add, added', Object.keys(data).length, 'fields');
        } catch (e) {}
      }
      perf.end('fetchIntercept');
    }
    return originalFetch.apply(this, arguments);
  };

  function init() {
    perf.start('totalInit');
    if (DEBUG_MODE) console.log('🚀 Attribution tracking initializing...');
    
    captureAttribution();
    
    if (window.location.pathname !== '/cart') {
      addAttributionToCart();
    }
    
    addAttributionToForms();
    
    const observer = new MutationObserver(addAttributionToForms);
    observer.observe(document.body, { 
      childList: true, 
      subtree: true 
    });
    
    perf.end('totalInit');
    if (DEBUG_MODE) console.log('✅ Attribution tracking ready');
  }

  // Enhanced debug tools
  window.attributionDebug = {
    get: getStoredAttribution,
    getMeta: captureMetaData,
    getAll: getAllData,
    
    test: function() {
      console.log('=== 📊 ATTRIBUTION TEST ===');
      console.table(getAllData());
      console.log('========================');
    },
    
    checkCart: async function() {
      const cart = await fetch('/cart.js').then(r => r.json());
      console.log('🛒 Cart Attributes:');
      console.table(cart.attributes || {});
      return cart.attributes;
    },
    
    performance: function() {
      const measures = performance.getEntriesByType('measure');
      console.log('⏱️ Performance Metrics:');
      console.table(measures.map(m => ({
        name: m.name,
        duration: `${m.duration.toFixed(2)}ms`
      })));
    },
    
    simulate: function(clickId) {
      const params = new URLSearchParams(window.location.search);
      params.set('gclid', clickId || 'test_gclid_' + Date.now());
      history.replaceState(null, '', '?' + params.toString());
      captureAttribution();
      console.log('✅ Simulated click, check with attributionDebug.test()');
    },
    
    clear: function() {
      localStorage.removeItem(ATTRIBUTION_CONFIG.storage.key);
      document.cookie = `${ATTRIBUTION_CONFIG.storage.key}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
      console.log('🗑️ Cleared all attribution data');
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    setTimeout(init, 0);
  }
})();